import { z } from 'zod';
import { addDays } from './facilities-preventive.ts';
import { fromScaled, toScaled } from './money.ts';
import { decimalString } from './schemas.ts';

/**
 * Caixa da Tesouraria (ADR 0084, Entrega 2a): extratos digitados, saldos informados pelo banco,
 * ajustes confirmados por outra pessoa e transferências. O cálculo do saldo do dia fica aqui, sem
 * acesso ao banco de dados, para ser o mesmo na tela, nos relatórios e nos testes.
 */

export const BANK_MOVEMENT_KINDS = [
  'payment',
  'receipt',
  'transfer',
  'fee',
  'interest',
  'tax',
  'fx',
  'other',
] as const;
export type BankMovementKind = (typeof BANK_MOVEMENT_KINDS)[number] | 'adjustment';

export const CASH_DIRECTIONS = ['in', 'out'] as const;
export type CashDirection = (typeof CASH_DIRECTIONS)[number];

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida');
const amount = decimalString(15, 4).refine(
  (v) => toScaled(v) > 0n,
  'O valor precisa ser maior que zero',
);
/** Saldo informado pelo banco: pode ser negativo. */
const signedAmount = z
  .string()
  .trim()
  .regex(/^-?\d{1,15}(\.\d{1,4})?$/, 'Número com até 4 casas decimais, usando ponto');
const reason = z.string().trim().min(3).max(1000);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const statementLineInput = z.object({
  valueDate: isoDate,
  direction: z.enum(CASH_DIRECTIONS),
  amount,
  description: z.string().trim().min(1).max(300),
  bankReference: optionalText(100),
  kind: z.enum(BANK_MOVEMENT_KINDS),
});
export type StatementLineInput = z.infer<typeof statementLineInput>;

/** Extrato digitado: as linhas de um período e, se houver, o saldo do fim do período. */
export const manualStatementInput = z
  .object({
    accountKey: z.uuid(),
    periodStart: isoDate,
    periodEnd: isoDate,
    lines: z.array(statementLineInput).max(500),
    closingBalance: signedAmount.nullable().optional(),
  })
  .refine((v) => v.periodEnd >= v.periodStart, {
    message: 'O fim do período não pode ser antes do início',
    path: ['periodEnd'],
  })
  .refine((v) => v.lines.every((l) => l.valueDate >= v.periodStart && l.valueDate <= v.periodEnd), {
    message: 'Todas as linhas precisam estar no período do extrato',
    path: ['lines'],
  });
export type ManualStatementInput = z.infer<typeof manualStatementInput>;

export const reportedBalanceInput = z.object({
  accountKey: z.uuid(),
  balanceDate: isoDate,
  amount: signedAmount,
  isOpening: z.boolean().default(false),
});

export const correctBalanceInput = z.object({ amount: signedAmount, reason });

export const balanceAdjustmentInput = z.object({
  accountKey: z.uuid(),
  adjustmentDate: isoDate,
  direction: z.enum(CASH_DIRECTIONS),
  amount,
  reason,
});

export const cashDecisionInput = z.object({ comment: optionalText(1000) });
export const cashReasonInput = z.object({ reason });
export const reverseBankMovementInput = z.object({ note: reason, valueDate: isoDate.optional() });

export const transferInput = z
  .object({
    fromAccountKey: z.uuid(),
    toAccountKey: z.uuid(),
    fromAmount: amount,
    /** Só quando as moedas das contas são diferentes. */
    toAmount: amount.nullable().optional(),
    exchangeRate: decimalString(10, 10).nullable().optional(),
    exchangeRateId: z.uuid().nullable().optional(),
    outDate: isoDate,
    inDate: isoDate.nullable().optional(),
    feeAmount: decimalString(15, 4).default('0'),
    description: z.string().trim().min(3).max(300),
  })
  .refine((v) => v.fromAccountKey !== v.toAccountKey, {
    message: 'Escolha duas contas diferentes',
    path: ['toAccountKey'],
  })
  .refine((v) => !v.inDate || v.inDate >= v.outDate, {
    message: 'A entrada não pode ser antes da saída',
    path: ['inDate'],
  });
export type TransferInput = z.infer<typeof transferInput>;

// ---------------------------------------------------------------------------
// Saldo do dia (ADR 0084, 3.3)
// ---------------------------------------------------------------------------

/** Um valor no dia, com sinal: entrada positiva, saída negativa. */
export interface DatedAmount {
  date: string;
  amount: string;
}

export interface DailyPositionSource {
  /** Saldo de abertura: o saldo no fim deste dia é o ponto de partida do cálculo. */
  opening: DatedAmount | null;
  /** Saldos informados pelo banco (os vigentes), no fim de cada dia. */
  reported: readonly DatedAmount[];
  /** Períodos cobertos por extrato gravado (inclusive). Neles, vale o extrato. */
  covered: readonly { start: string; end: string }[];
  /** Linhas de extrato (inclusive estornos), com sinal. */
  statement: readonly DatedAmount[];
  /** Ajustes confirmados, com sinal. Valem em qualquer dia. */
  adjustments: readonly DatedAmount[];
  /** Baixas dos módulos (pagamentos, estornos, transferências), com sinal. Valem sem extrato. */
  settlements: readonly DatedAmount[];
}

export type DailyPositionStatus = 'before_opening' | 'pending' | 'reconciled' | 'divergent';

export interface DailyPosition {
  date: string;
  covered: boolean;
  /** Saldo inicial: o calculado do dia anterior (ou nulo antes da abertura). */
  opening: string | null;
  credits: string;
  debits: string;
  /** Saldo final calculado = inicial + créditos − débitos. */
  calculated: string | null;
  reported: string | null;
  /** Informado − calculado, quando há os dois. */
  difference: string | null;
  status: DailyPositionStatus;
  /** Houve ajuste confirmado no dia. */
  adjusted: boolean;
  /** Baixas de módulos num dia coberto por extrato: não somam (viram divergência na conciliação). */
  settlementsOnCoveredDay: string;
}

function sumByDay(items: readonly DatedAmount[]): Map<string, { credits: bigint; debits: bigint }> {
  const out = new Map<string, { credits: bigint; debits: bigint }>();
  for (const it of items) {
    const v = toScaled(it.amount);
    const cur = out.get(it.date) ?? { credits: 0n, debits: 0n };
    if (v >= 0n) cur.credits += v;
    else cur.debits -= v;
    out.set(it.date, cur);
  }
  return out;
}

/**
 * Saldo dia a dia de uma conta, de `from` a `to` (inclusive). O cálculo parte do saldo de
 * abertura e segue dia a dia: um dia sem saldo informado fica pendente (nunca zero), e a
 * diferença com o banco não é corrigida sozinha: só um ajuste confirmado a resolve.
 */
export function dailyPositions(
  source: DailyPositionSource,
  from: string,
  to: string,
): DailyPosition[] {
  const statement = sumByDay(source.statement);
  const adjustments = sumByDay(source.adjustments);
  const settlements = sumByDay(source.settlements);
  const reported = new Map(source.reported.map((r) => [r.date, toScaled(r.amount)]));
  const isCovered = (d: string) => source.covered.some((c) => c.start <= d && d <= c.end);
  const zero = { credits: 0n, debits: 0n };

  const rows: DailyPosition[] = [];
  const anchor = source.opening;
  let balance: bigint | null = null;
  const start = anchor && anchor.date < from ? anchor.date : from;
  for (let d = start; d <= to; d = addDays(d, 1)) {
    const covered = isCovered(d);
    const st = statement.get(d) ?? zero;
    const adj = adjustments.get(d);
    const se = settlements.get(d) ?? zero;
    const base = covered ? st : se;
    const credits = base.credits + (adj?.credits ?? 0n);
    const debits = base.debits + (adj?.debits ?? 0n);
    let opening: bigint | null = null;
    let calculated: bigint | null = null;
    if (anchor && d === anchor.date) {
      calculated = toScaled(anchor.amount);
    } else if (balance !== null) {
      opening = balance;
      calculated = balance + credits - debits;
    }
    balance = calculated;
    if (d < from) continue;
    const rep = reported.get(d) ?? null;
    const difference = rep !== null && calculated !== null ? rep - calculated : null;
    rows.push({
      date: d,
      covered,
      opening: opening === null ? null : fromScaled(opening),
      credits: fromScaled(credits),
      debits: fromScaled(debits),
      calculated: calculated === null ? null : fromScaled(calculated),
      reported: rep === null ? null : fromScaled(rep),
      difference: difference === null ? null : fromScaled(difference),
      status:
        calculated === null
          ? 'before_opening'
          : rep === null
            ? 'pending'
            : difference === 0n
              ? 'reconciled'
              : 'divergent',
      adjusted: !!adj,
      settlementsOnCoveredDay: covered ? fromScaled(se.credits - se.debits) : '0.0000',
    });
  }
  return rows;
}

/** Valor com sinal: entrada positiva, saída negativa. */
export function signedByDirection(direction: CashDirection, value: string): string {
  return direction === 'in' ? value : fromScaled(-toScaled(value));
}
